import React, { useRef, useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import styles from "./NewsRoom.module.css";
import RightArrow from "../../assets/images/icons/rgt_arrow_vector_bl.png";
import Container from "../../Components/General/Container";
import MediaInvestor from "./MediaInvestor";
import MediaCardMobileView from "./MediaCardMobileView";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase/config";
import AwardsRow from "../../Components/General/AwardsRow";
import { LogoContainer } from "../../Components/General/ClientLogos";

// Re-exported for the homepage, which has imported it from here historically.
export { LogoContainer };

const Card = ({ children, className }) => (
  <div className={`${styles.card} ${className}`}>{children}</div>
);

const Section = ({ title, children }) => (
  <motion.section
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.8 }}
    className={styles.newsroomSection}
  >
    <h2 className={styles.sectionTitle}>{title}</h2>
    <div className={styles.sectionContent}>{children}</div>
  </motion.section>
);

export const MediaRow = ({ articles }) => {
  const scrollRef = useRef(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(true);

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftFade(scrollLeft > 0);
      setShowRightFade(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener("scroll", handleScroll);
      handleScroll();
      return () => scrollElement.removeEventListener("scroll", handleScroll);
    }
  }, []);

  return (
    <>
      <div className={`${styles.mediaRowContainer} ${styles.desktopView}`}>
        {showLeftFade && <div className={styles.gradientLeft}></div>}
        {showRightFade && <div className={styles.gradientRight}></div>}
        <div className={styles.mediaRow} ref={scrollRef}>
          {articles.map((article, index) => (
            <a
              key={index}
              href={article.redirectLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <motion.div
                className={styles.articleCard}
                whileHover={{ scale: 1.05 }}
              >
                <Card>
                  <div className={styles.articleContent}>
                    <img
                      src={article.image}
                      alt={article.title}
                      className={styles.articleImage}
                    />
                    <p className={styles.articleTitle}>{article.title}</p>
                  </div>
                  <div className={styles.articleMeta}>
                    <div
                      className={styles.mediaRedirectBtn}
                      id="Media_Section7_MediaMoments_Card1"
                    >
                      <span className={styles.readMoreText}>Read More</span>
                      <img src={RightArrow} alt="arrow" />
                    </div>
                  </div>
                </Card>
              </motion.div>
            </a>
          ))}
        </div>
      </div>
      <MediaCardMobileView displayItems={articles} />
    </>
  );
};

const NewsRoom = () => {
  const [mediaArticles, setMediaArticles] = useState([]);
  const [accolades, setAccolades] = useState([]);

  useEffect(() => {
    const unsubMedia = onSnapshot(collection(db, "mediaArticles"), (snap) => {
      setMediaArticles(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    const unsubAccolades = onSnapshot(collection(db, "accolades"), (snap) => {
      setAccolades(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => { unsubMedia(); unsubAccolades(); };
  }, []);

  return (
    <AnimatePresence>
      <Container>
        <div className={styles.titleContainer}>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.8 }}
            className={styles.title}
          >
            <span style={{ color: "#1579be" }} className={styles.title}>
              NowPurchase
            </span>{" "}
            Newsroom
          </motion.h1>
        </div>

        <Section
          title={
            <>
              Our <span className={styles.skyText}>Media</span> Moments!
            </>
          }
        >
          <MediaRow articles={mediaArticles} />
        </Section>

        <Section
          title={
            <>
              Our <span className={styles.skyText}>Accolades</span> &{" "}
              <span className={styles.skyText}>Associations</span>
            </>
          }
        >
          <AwardsRow items={accolades} />
        </Section>
      </Container>
      <MediaInvestor />
    </AnimatePresence>
  );
};

export default NewsRoom;
